import { Button, makeStyles, Text, Tooltip, tokens } from '@fluentui/react-components';
import { CheckmarkRegular, CopyRegular } from '@fluentui/react-icons';
import React, { useEffect, useRef, useState } from 'react';
import { copyCorrelationId } from './scrub-correlation-id';

const COPIED_RESET_MS = 1500;

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXXS,
  },
  id: {
    fontFamily: tokens.fontFamilyMonospace,
  },
});

interface ScrubCorrelationIdProps {
  correlationId: string;
  label?: string;
}

export const ScrubCorrelationId: React.FC<ScrubCorrelationIdProps> = ({ correlationId, label = 'ID:' }) => {
  const styles = useStyles();
  const [copied, setCopied] = useState(false);
  const mountedRef = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current !== undefined) clearTimeout(timerRef.current);
    };
  }, []);

  const onCopy = async (): Promise<void> => {
    const succeeded = await copyCorrelationId(correlationId);
    if (!succeeded || !mountedRef.current) return;
    setCopied(true);
    if (timerRef.current !== undefined) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (mountedRef.current) setCopied(false);
    }, COPIED_RESET_MS);
  };

  return (
    <span className={styles.root}>
      <Text size={200}>{label}</Text>
      <Text size={200} className={styles.id}>{correlationId}</Text>
      <Tooltip content={copied ? 'Copied' : 'Copy diagnostics id'} relationship='label'>
        <Button
          appearance='transparent'
          size='small'
          icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
          onClick={onCopy}
        />
      </Tooltip>
    </span>
  );
};
